import React, { useEffect, useState } from 'react';
import { Autocomplete, createFilterOptions, Grid } from '@mui/material';
import { SizeProps } from '../../../@types';
import { SelectMenu } from '../../atoms/SelectMenu';
import { TextField } from '../../atoms/TextField';
import { ManualAddressProps, OptionType } from './ManualAddress.props';
import { isEmptyString } from '../../../utils/common';
import { GRID_SPACING } from '../../../configs/ui-constants';

export const ManualAddress: React.FC<ManualAddressProps> = ({
  onChange,
  error,
  helperText,
  manualAddressData,
  addressTypeData,
  disableTypeSelection = false,
  countries,
  states,
  enableContactNumber,
  enableAddressContactFields,
  hasAddressEditPermission,
  isClientCustomerStructureCreation
}) => {
  const [countryValue, setCountryValue] = useState<OptionType | null>(null);
  const [stateValue, setStateValue] = useState<OptionType | null>(null);
  
  useEffect(() => {
    manualAddressData?.countryName && setCountryValue(countries.find(item => item.name === manualAddressData.countryName) || null);
  }, [manualAddressData?.countryName, countries]);

  useEffect(() => {
    manualAddressData?.stateName && setStateValue(states?.find(item => item.name === manualAddressData.stateName) || null);
  }, [manualAddressData?.stateName, states]);

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: OptionType) => option.name,
  });

  return (
    <>
      <Grid container spacing={GRID_SPACING}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <SelectMenu
            id="page-size-menu"
            minWidth={'100%'}
            items={addressTypeData}
            disabled={disableTypeSelection || !hasAddressEditPermission}
            selectedValue={manualAddressData.addressType}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                addressType: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            disabled={!hasAddressEditPermission}
            label={enableAddressContactFields ? 'Contact Name': 'Name'}
            error={error && !manualAddressData?.name}
            helperText={helperText?.nameHelperText}
            value={manualAddressData?.name}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                name: text
              });
            }}
            required={isClientCustomerStructureCreation}
          />
        </Grid>
        {enableContactNumber && 
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <TextField
              fullWidth
              disabled={!hasAddressEditPermission}
              required={!enableAddressContactFields || isClientCustomerStructureCreation}
              label="Contact Number"
              error={(!enableAddressContactFields && isEmptyString(manualAddressData?.contactNumber?.toString())) || (error && !manualAddressData?.contactNumber)}
              helperText={helperText?.contactNoHelperText}
              value={manualAddressData?.contactNumber}
              onChange={(text) => {
                onChange({
                  ...manualAddressData,
                  contactNumber: text
                });
              }}
            />
          </Grid>
        }
        {enableAddressContactFields && 
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <TextField
              fullWidth
              disabled={!hasAddressEditPermission}
              label="Contact Company"
              value={manualAddressData?.contactCompany}
              onChange={(text) => {
                onChange({
                  ...manualAddressData,
                  contactCompany: text
                });
              }}
              required={isClientCustomerStructureCreation}
              error={error && !manualAddressData?.contactCompany}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            required
            disabled={!hasAddressEditPermission}
            label="Line 1"
            error={isEmptyString(manualAddressData?.line1 as string)}
            helperText={helperText?.line1HelperText}
            value={manualAddressData?.line1}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                line1: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            disabled={!hasAddressEditPermission}
            label="Line 2"
            helperText={helperText?.line2HelperText}
            value={manualAddressData?.line2}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                line2: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            required
            disabled={!hasAddressEditPermission}
            label="Suburb"
            error={isEmptyString(manualAddressData?.suburb as string)}
            helperText={helperText?.suburbHelperText}
            value={manualAddressData?.suburb}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                suburb: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            disabled={!hasAddressEditPermission}
            label="City"
            helperText={helperText?.cityHelperText}
            value={manualAddressData?.city}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                city: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            required
            disabled={!hasAddressEditPermission}
            label="Postcode"
            error={isEmptyString(manualAddressData?.postCode as string)}
            helperText={helperText?.postCodeHelperText}
            value={manualAddressData?.postCode}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                postCode: text
              });
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Autocomplete
            id="country-filter"
            disabled={!hasAddressEditPermission}
            options={countries}
            getOptionLabel={(option) => option.name}
            filterOptions={filterOptions}
            onChange={(event, data) => {
              setCountryValue(data);
              onChange({
                ...manualAddressData,
                countryId: data?.id,
                countryName: data?.name
              });
            }}
            value={countryValue}
            renderInput={(params) => (
              <TextField
                {...params}
                disabled={!hasAddressEditPermission}
                label="Country"
                required
                size={SizeProps.Small}
                error={isEmptyString(manualAddressData?.countryName as string)}
                helperText={helperText?.countryDataHelperText}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Autocomplete
            id="state-filter"
            disabled={!hasAddressEditPermission}
            options={states}
            getOptionLabel={(option) => option.name}
            filterOptions={filterOptions}
            onChange={(event, data) => {
              setStateValue(data);
              onChange({
                ...manualAddressData,
                stateId: data?.id,
                stateName: data?.name
              });
            }}
            value={stateValue}
            renderInput={(params) => (
              <TextField
                {...params}
                disabled={!hasAddressEditPermission}
                label="State"
                required
                size={SizeProps.Small}
                error={isEmptyString(manualAddressData?.stateName as string)}
                helperText={helperText?.stateDataHelperText}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            disabled={!hasAddressEditPermission}
            label="Traditional Place Name"
            helperText={helperText?.placeNameHelperText}
            value={manualAddressData?.placeName}
            onChange={(text) => {
              onChange({
                ...manualAddressData,
                placeName: text
              });
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};